import { RowSelectionState, SortingState } from "@tanstack/react-table";
import filter from "lodash/filter";
import find from "lodash/find";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import pick from "lodash/pick";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { getProfileFrameworkSkills } from "PFApp/profiles/show/profile_show_page.utils";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { useProfileSkillsFrameworks } from "PFCore/hooks/queries/skills_frameworks/use_profile_skills_frameworks";
import { Profile } from "PFCore/types";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAccordionTrigger } from "../../hooks/use_accordion_trigger";
import css from "../../skills_section.module.scss";
import { SkillsViewCommonProps } from "../../skills_section.types";
import { filterSkillsByTerm, getAllFrameworksUniqueSkillsList } from "../../skills_section.utils";
import { SkillsEmptyState } from "../skills_empty_state";
import { SkillsGroupAccordion } from "../skills_group_accordion";

type SkillsByFrameworkViewProps = {
  profile: Profile;
  searchTerm: string;
} & SkillsViewCommonProps;

export const SkillsByFrameworkView = ({
  profile,
  searchTerm,
  setSearchTerm,
  rowSelectionState,
  ...props
}: SkillsByFrameworkViewProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts.skillsSection" });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { stateTrigger, handleStateChange } = useAccordionTrigger(searchTerm);

  const { data, isLoading } = useProfileSkillsFrameworks({
    profileId: profile.id,
    params: { page: 1, perPage: PER_PAGE_UNPAGINATED }
  });

  const [skillsFrameworksToDisplay, noFrameworkSkills] = useMemo(() => {
    const skillsFrameworks = orderBy(data?.entries || [], "name");
    const skillsFrameworksToDisplay = filter(
      map(skillsFrameworks, (framework) => ({
        ...framework,
        skills: orderBy(filterSkillsByTerm(getProfileFrameworkSkills(framework, profile), searchTerm), [
          ({ experience }) => !!experience,
          "value"
        ])
      })),
      "skills.length"
    );

    const allFrameworksSkills = getAllFrameworksUniqueSkillsList(skillsFrameworks);
    const noFrameworkSkills = filter(
      filterSkillsByTerm(profile.skills || [], searchTerm),
      ({ id }) => !find(allFrameworksSkills, ["skillId", id])
    );

    return [skillsFrameworksToDisplay, noFrameworkSkills];
  }, [data?.entries, profile, searchTerm]);

  if (!skillsFrameworksToDisplay.length && !noFrameworkSkills.length) {
    return <SkillsEmptyState searchTerm={searchTerm} setSearchTerm={setSearchTerm} />;
  }

  return (
    <Stack gap="spacingLg">
      {skillsFrameworksToDisplay.map((framework) => {
        const { id, name, skills } = framework;
        const profileSkills = skills.filter(({ experience }) => !!experience);
        const frameworkRowSelectionState = pick(rowSelectionState, map(skills, "id")) as RowSelectionState;

        return (
          <Fragment key={id}>
            <SkillsGroupAccordion
              {...props}
              header={`${name} (${profileSkills.length}/${skills.length})`}
              state={stateTrigger}
              onStateChange={handleStateChange}
              skills={skills}
              controlledSorting={sorting}
              onSortingChange={setSorting}
              profileId={profile.id}
              rowSelectionState={frameworkRowSelectionState}
            />
            <Divider className={css.groupDivider} />
          </Fragment>
        );
      })}
      {!isLoading && noFrameworkSkills.length > 0 && (
        <>
          <SkillsGroupAccordion
            {...props}
            header={t("noFramework", { count: noFrameworkSkills.length })}
            state={stateTrigger}
            onStateChange={handleStateChange}
            skills={noFrameworkSkills}
            controlledSorting={sorting}
            onSortingChange={setSorting}
            profileId={profile.id}
            rowSelectionState={pick(rowSelectionState, map(noFrameworkSkills, "id")) as RowSelectionState}
          />
          <Divider className={css.groupDivider} />
        </>
      )}
    </Stack>
  );
};
